/**
 * Desktop view (`lg` and above) of the bulk-action bar, rendered by `./ActionBar.tsx`: the
 * "Unselect All" control and selected count on the left (only while rows are selected), the action
 * items inline on the right. Ignores `mobileActionItems` and `mobileContainerCss`. Hidden when
 * printing.
 */
import React from "react";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Typography } from "@ryerson/frontend.typography";
import { Icon, IconList } from "@ryerson/frontend.assets";
import { useTheme } from "@ryerson/frontend.theme";
import { ActionBarProps } from "./ActionBarTypes";
import { ActionBarContentProps } from "./ActionBarContent";

type ActionBarDesktopProps = ActionBarProps & {
	content: ActionBarContentProps;
};

const ActionBarContainer = styled(Flex)`
	padding: 20px 10px;
	border-radius: 2px 2px 0px 0px;
	width: 100%;
	box-sizing: border-box;
	${(props: any) => {
		const { color } = props;
		return css`
			background: ${color};
		`;
	}};

	@media print {
		display: none;
	}
`;

/** Desktop bulk-action bar. Clicking "Unselect All" calls `setSelectedRows([])`. */
const ActionBarDesktop: React.FC<ActionBarDesktopProps> = ({
	selectedRows,
	setSelectedRows,
	actionItems,
	preContent,
	content,
}) => {
	const { theme } = useTheme();
	const { white, darkerGray, lightGray } = theme.colors.primary;

	return (
		<>
			{preContent}
			<ActionBarContainer {...{ color: darkerGray }} justifyContent="space-between" dataTestId="action-bar-container">
				<Flex alignItems="center">
					{selectedRows.length > 0 && (
						<FlexItem>
							<Flex
								onClick={() => setSelectedRows([])}
								alignItems="center"
								dataTestId="action-bar-unselect-all-button"
							>
								<Icon
									size="sm"
									icon="close"
									circled={true}
									color={white}
									css={{ marginRight: "5px" }}
								/>
								<Typography variant="span" type="tertiary" color={white} size="sm">
									{content.unselectAll}
								</Typography>
								<Typography
									type="tertiary"
									size="sm"
									variant="div"
									css={css`
										margin-left: 20px;
										border-left: 1px solid ${lightGray};
										padding-left: 20px;
									`}
								>
									{content.selected} &nbsp;
									<Typography weight="bold" color={white}>
										{selectedRows.length}
									</Typography>
									&nbsp;
									{selectedRows.length > 1 ? content.products : content.product}
								</Typography>
							</Flex>
						</FlexItem>
					)}
				</Flex>
				<Flex alignItems="center">
					{actionItems.map((item) => {
						// A disabled item is made unclickable by the wrapper's pointer-events, not
						// by dropping its onClick. The icon stays lightGray either way; only the
						// label changes colour.
						const isDisabled =
							"isDisabled" in item ? (item.isDisabled ?? false) : false;
						return (
							<FlexItem
								key={item.testId}
								css={{ pointerEvents: isDisabled ? "none" : "all" }}
							>
								{"renderJSX" in item ? (
									item.renderJSX()
								) : (
									<Flex
										onClick={item.onClick}
										alignItems="center"
										css={{ cursor: "pointer", margin: "0px 10px" }}
										dataTestId={`action-bar-${item.testId}-button`}
									>
										<Icon
											size={item.isIconCircled ? "md" : "sm"}
											icon={item.icon as keyof IconList}
											circled={item.isIconCircled}
											color={isDisabled ? lightGray : lightGray}
											css={{
												flexShrink: 0,
												marginRight: "10px",
												cursor: "pointer",
											}}
										/>
										<Typography
											variant="span"
											color={isDisabled ? lightGray : white}
											size="sm"
										>
											{item.title}
										</Typography>
									</Flex>
								)}
							</FlexItem>
						);
					})}
				</Flex>
			</ActionBarContainer>
		</>
	);
};

export default ActionBarDesktop;
